/**
 * CreateCampaignTab Component
 * 
 * Upload CSV, validate, preview data, and launch or save as draft
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { Upload, FileText, AlertCircle, X, CheckCircle2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import CsvPreviewTable from './CsvPreviewTable';
import CostSummaryCard from './CostSummaryCard';
import { validateCsvData } from '../../utils/csvValidation';
import { launchCampaign, saveDraft } from '../../api/outboundApi';
import { useSettings } from '../../contexts/SettingsContext';
import { useUser } from '../../hooks/useUser';
import useHubkomUsage from '../../hooks/useHubkomUsage';

const CreateCampaignTab = ({ onSwitchTab }) => {
  const { selectedLocationId } = useSettings();
  const { isHubkomClient } = useUser();
  const { data: hubkomUsage } = useHubkomUsage(isHubkomClient ? selectedLocationId : null);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Auto-dismiss success messages
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setCsvFile(file);
    setIsProcessing(true);
    setValidationErrors([]);
    setParsedData([]);

    // Parse CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Add row numbers for error reporting
        const dataWithRowNumbers = results.data.map((row, index) => ({
          ...row,
          rowNumber: index + 2, // +2 because row 1 is header, and we want 1-indexed
        }));

        // Filter out completely empty rows
        const nonEmptyRows = dataWithRowNumbers.filter(row => {
          // Check if row has at least one non-empty value
          return Object.keys(row).some(key => {
            if (key === 'rowNumber') return false; // Ignore rowNumber field
            const value = row[key];
            return value && typeof value === 'string' && value.trim() !== '';
          });
        });

        // Validate data
        const { isValid: valid, errors } = validateCsvData(nonEmptyRows);
        
        setParsedData(nonEmptyRows);
        setValidationErrors(errors);
        setIsValid(valid);
        setIsProcessing(false);
      },
      error: () => {
        setValidationErrors([{ message: 'Failed to parse CSV file. Please check the format.' }]);
        setIsProcessing(false);
      },
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
  });

  // Remove uploaded file
  const handleRemoveFile = () => {
    setCsvFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setIsValid(false);
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    // Create CSV with headers and 4 empty rows
    const headers = [
      'Phone Number',
      'First Name',
      'Last Name',
      'DOB',
      'Appointment Date',
      'Appointment Time',
      'Doctor'
    ];
    
    // Headers + 4 empty rows for easy fill-out
    const rows = [
      headers.join(','),
      ',,,,,,',  // Empty row 1
      ',,,,,,',  // Empty row 2
      ',,,,,,',  // Empty row 3
      ',,,,,,',  // Empty row 4
    ];
    
    const csvContent = rows.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'outbound-campaign-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Save as draft
  const handleSaveDraft = async () => {
    if (!selectedLocationId || selectedLocationId === 'all') {
      setActionError('Please select a specific location before saving a draft.');
      return;
    }

    setIsSaving(true);
    setActionError(null);
    try {
      const campaignName = `Campaign - ${new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;

      await saveDraft({
        name: campaignName,
        locationId: selectedLocationId,
        csvData: parsedData,
        totalAppointments: parsedData.length,
        estimatedCost: totalCost
      });

      setActionSuccess('Draft saved successfully!');
      handleRemoveFile();

      if (onSwitchTab) {
        onSwitchTab('drafts');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setActionError(error.message || 'Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Launch campaign
  const handleLaunchCampaign = async () => {
    if (!selectedLocationId || selectedLocationId === 'all') {
      setActionError('Please select a specific location before launching a campaign.');
      return;
    }

    if (!window.confirm(`Are you sure you want to launch this campaign with ${parsedData.length} appointments?\n\nCost: $${totalCost.toFixed(2)} upfront`)) {
      return;
    }

    setIsLaunching(true);
    setActionError(null);
    try {
      const campaignName = `Campaign - ${new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;

      await launchCampaign({
        name: campaignName,
        locationId: selectedLocationId,
        csvData: parsedData,
        totalAppointments: parsedData.length
      });

      setActionSuccess('Campaign launched successfully!');
      handleRemoveFile();

      if (onSwitchTab) {
        onSwitchTab('history');
      }
    } catch (error) {
      console.error('Error launching campaign:', error);

      if (error.validationErrors) {
        const errorMessages = error.validationErrors.map(e =>
          `Row ${e.row}: ${e.message}`
        ).join('; ');
        setActionError(`CSV Validation Failed: ${errorMessages}`);
      } else {
        setActionError(error.message || 'Failed to launch campaign. Please try again.');
      }
    } finally {
      setIsLaunching(false);
    }
  };

  const totalCost = parsedData.length * 0.50;

  return (
    <div className="space-y-6">
      {/* Action Error Banner */}
      {actionError && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400 flex-1">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Action Success Banner */}
      {actionSuccess && (
        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-400 flex-1">{actionSuccess}</p>
          <button onClick={() => setActionSuccess(null)} className="text-green-400 hover:text-green-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* File Upload Area */}
      {!csvFile && (
        <Card className="bg-gray-800/50 border-gray-700 p-8">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors
              ${isDragActive 
                ? 'border-[#3AD0E6] bg-[#3AD0E6]/10' 
                : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/30'
              }
            `}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-white mb-2">
              {isDragActive ? 'Drop CSV file here' : 'Upload CSV File'}
            </h3>
            <p className="text-gray-400 mb-4">
              Drag and drop your CSV file here, or click to browse
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Required columns: Phone Number, First Name, Last Name, DOB, Appointment Date, Appointment Time, Doctor
            </p>
            <Button variant="outline" className="mt-2">
              Browse Files
            </Button>
          </div>

          {/* Template Download Button */}
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              className="text-[#3AD0E6] hover:text-[#3AD0E6]/80"
              onClick={handleDownloadTemplate}
            >
              <FileText className="w-4 h-4 mr-2" />
              Download Template CSV
            </Button>
          </div>
        </Card>
      )}

      {/* File Uploaded State */}
      {csvFile && (
        <>
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#3AD0E6]" />
                <div>
                  <h4 className="text-white font-medium">{csvFile.name}</h4>
                  <p className="text-sm text-gray-400">
                    {parsedData.length} appointments found
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </Card>

          {/* Processing State */}
          {isProcessing && (
            <Card className="bg-gray-800/50 border-gray-700 p-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6] mr-4"></div>
                <p className="text-gray-400">Processing CSV file...</p>
              </div>
            </Card>
          )}

          {/* Validation Errors */}
          {!isProcessing && validationErrors.length > 0 && (
            <Card className="bg-red-900/20 border-red-700 p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-white font-medium mb-2">
                    Validation Errors Found ({validationErrors.length} {validationErrors.length === 1 ? 'error' : 'errors'})
                  </h4>
                  <div className="space-y-1">
                    {validationErrors.slice(0, 10).map((error, index) => (
                      <p key={index} className="text-sm text-red-300">
                        • {error.message}
                      </p>
                    ))}
                    {validationErrors.length > 10 && (
                      <p className="text-sm text-red-300 italic">
                        ... and {validationErrors.length - 10} more errors
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-3">
                    Please fix these errors and re-upload the CSV file.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Success - Show Preview and Cost */}
          {!isProcessing && isValid && parsedData.length > 0 && (
            <>
              {/* Cost Summary or Remaining Dials */}
              {isHubkomClient && hubkomUsage ? (
                <Card className="bg-gray-800/50 border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Outbound Dials</h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">
                      {hubkomUsage.outbound.dialsUsed} of {hubkomUsage.outbound.dialsLimit} dials used this month
                    </span>
                    <span className="text-sm font-medium text-white">
                      {Math.max(0, hubkomUsage.outbound.dialsLimit - hubkomUsage.outbound.dialsUsed)} remaining
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        hubkomUsage.outbound.dialsUsed / hubkomUsage.outbound.dialsLimit > 0.9
                          ? 'bg-red-500' : 'bg-[#3AD0E6]'
                      }`}
                      style={{ width: `${Math.min((hubkomUsage.outbound.dialsUsed / hubkomUsage.outbound.dialsLimit) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-400">
                    This campaign will use <span className="text-white font-medium">{parsedData.length}</span> dials
                  </p>
                  {(() => {
                    const remaining = hubkomUsage.outbound.dialsLimit - hubkomUsage.outbound.dialsUsed;
                    const exceeds = parsedData.length > remaining;
                    if (exceeds && !hubkomUsage.outbound.allowOverage) {
                      return (
                        <div className="mt-3 bg-yellow-900/20 border border-yellow-700 rounded-lg p-3">
                          <p className="text-sm text-yellow-400">
                            This campaign has {parsedData.length} dials but you only have {Math.max(0, remaining)} remaining.
                            The remaining calls will not be placed. Enable overage on the Usage page to allow all calls.
                          </p>
                        </div>
                      );
                    }
                    if (exceeds && hubkomUsage.outbound.allowOverage) {
                      const overageDials = parsedData.length - remaining;
                      const overageCost = overageDials * hubkomUsage.outbound.overageRate;
                      return (
                        <div className="mt-3 bg-blue-900/20 border border-blue-700 rounded-lg p-3">
                          <p className="text-sm text-blue-400">
                            This will use {overageDials} overage dials at ${hubkomUsage.outbound.overageRate.toFixed(2)}/dial
                            (est. ${overageCost.toFixed(2)} overage cost).
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </Card>
              ) : (
                <CostSummaryCard
                  totalAppointments={parsedData.length}
                  upfrontCost={totalCost}
                />
              )}

              {/* Preview Table */}
              <CsvPreviewTable data={parsedData} />

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isSaving || isLaunching}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  {isSaving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button
                  onClick={handleLaunchCampaign}
                  disabled={isSaving || isLaunching}
                  className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white hover:opacity-90"
                >
                  {isLaunching ? 'Launching...' : 'Confirm & Launch Campaign →'}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CreateCampaignTab;

